import { useState } from "react";
import styles from "./SyncIndicator.module.css";

export type SyncIndicatorConnection =
  | "connected"
  | "connecting"
  | "offline"
  | "error";

export interface SyncIndicatorProps {
  /** Whether server changes can currently reach us. */
  connection: SyncIndicatorConnection;
  /** True while a local change has not been confirmed by the server. */
  pending: boolean;
  /** ISO-8601 of the last moment we were connected and fully in sync. */
  lastInSyncAt?: string | null;
  "data-testid"?: string;
}

/**
 * Reports two independent facts, always both, because neither answers the
 * user's question alone:
 *
 * 1. **Tilkoblet / Frakoblet / Synkroniseringsfeil** — can anything reach us.
 * 2. **Alt overført / Endringer ikke overført** — has our own work left this
 *    device.
 *
 * The second is deliberately one-directional. While disconnected we know only
 * that our outbound queue is empty; claiming anything about what we have
 * *received* would be a guess. Slot 1 then disambiguates slot 2 for free:
 * connected and not transferred means it is being sent right now, disconnected
 * and not transferred means it is waiting for the network.
 */
export function SyncIndicator({
  connection,
  pending,
  lastInSyncAt,
  "data-testid": testId,
}: SyncIndicatorProps) {
  const [expanded, setExpanded] = useState(false);

  /**
   * While we do not yet know, `pending` is not ours to report either — it
   * comes from the same place. So the second slot is dropped rather than
   * guessed, and no colour is claimed.
   */
  const unknown = connection === "connecting";
  const disconnected = !unknown && connection !== "connected";
  const unsaved = disconnected && pending;
  const state = unknown
    ? "connecting"
    : disconnected
      ? connection
      : pending
        ? "saving"
        : "saved";

  const label = unknown
    ? CONNECTION_LABEL.connecting
    : `${CONNECTION_LABEL[connection]} · ${
        pending ? "Endringer ikke overført" : "Alt overført"
      }`;

  const stamp = formatStamp(lastInSyncAt);

  return (
    <div className={styles.wrap}>
      <div
        className={styles.indicator}
        data-testid={testId}
        data-state={state}
        data-unsaved={unsaved ? "true" : undefined}
        role="status"
        aria-live="polite"
      >
        <span className={styles.icon} aria-hidden="true">
          <StateIcon state={state} unsaved={unsaved} />
        </span>
        <span className={styles.label}>{label}</span>
        {/* Offered in every state, so the bar keeps one shape and the healthy
            case can say what it means rather than only being green. */}
        <button
          type="button"
          className={styles.expand}
          aria-expanded={expanded}
          aria-label={expanded ? "Skjul forklaring" : "Vis forklaring"}
          data-testid={testId ? `${testId}-expand` : undefined}
          onClick={() => setExpanded((open) => !open)}
        >
          <ChevronIcon flipped={expanded} />
        </button>
      </div>
      {expanded && (
        <p
          className={styles.explanation}
          data-testid={testId ? `${testId}-explanation` : undefined}
        >
          {explain(connection, pending)}
          {disconnected && stamp
            ? ` Sist i kontakt med serveren kl. ${stamp}.`
            : ""}
        </p>
      )}
    </div>
  );
}

const CONNECTION_LABEL: Record<SyncIndicatorConnection, string> = {
  connected: "Tilkoblet",
  connecting: "Kobler til …",
  offline: "Frakoblet",
  error: "Synkroniseringsfeil",
};

function explain(
  connection: SyncIndicatorConnection,
  pending: boolean,
): string {
  if (connection === "connecting") {
    // The realistic cause, and the one that is not obvious: another tab holds
    // the synchronisation and has not reported in. An older build of the app
    // does not report at all, so it can hold this state indefinitely.
    return "Appen finner ut av synkroniseringen. Blir dette stående, har sannsynligvis en annen fane tatt over jobben — lukk de andre fanene og last denne på nytt.";
  }
  if (connection === "connected") {
    return pending
      ? "Endringene dine er lagret på denne enheten og sendes til serveren nå. Det tar vanligvis et øyeblikk."
      : "Alt du har gjort ligger på serveren, og endringer fra andre enheter kommer inn med én gang.";
  }
  const offline = connection === "offline";
  const cause = offline
    ? "Enheten er uten nett."
    : "Synkroniseringen feiler, men enheten prøver igjen av seg selv.";
  // What has to happen before this resolves. Waiting for the network only
  // makes sense when the network is what is missing — with a sync failure the
  // connection is already there, so promising its return would be a lie.
  const recovery = offline
    ? "enheten er på nett igjen"
    : "synkroniseringen virker igjen";
  return pending
    ? `${cause} Endringene dine er trygt lagret her, men ikke sendt videre ennå. De sendes automatisk når ${recovery}. Andre enheter ser dem ikke før det har skjedd.`
    : `${cause} Alt du har gjort er allerede sendt, så ingenting går tapt. Men endringer fra andre enheter når deg ikke før ${recovery}.`;
}

function formatStamp(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
}

function StateIcon({ state, unsaved }: { state: string; unsaved: boolean }) {
  if (unsaved || state === "error") return <AlertIcon />;
  if (state === "offline") return <WifiOffIcon />;
  if (state === "connecting") return <SyncingIcon />;
  if (state === "saving") return <UploadIcon />;
  return <CheckIcon />;
}

function SyncingIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
      <path
        d="M20 12a8 8 0 1 1-2.34-5.66"
        stroke="currentColor"
        strokeWidth="2.3"
        strokeLinecap="round"
      />
      <path
        d="M20 4v5h-5"
        stroke="currentColor"
        strokeWidth="2.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
      <path
        d="M4.5 12.5 9.5 17.5 19.5 7"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function UploadIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
      <path
        d="M12 19V5"
        stroke="currentColor"
        strokeWidth="2.3"
        strokeLinecap="round"
      />
      <path
        d="m6 11 6-6 6 6"
        stroke="currentColor"
        strokeWidth="2.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function WifiOffIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
      <path
        d="M3 3l18 18"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
      />
      <path
        d="M5 12.5a11 11 0 0 1 4-2.6M2 8.8A16 16 0 0 1 9 5.6m6 .1a16 16 0 0 1 7 3.1M8.5 16a6 6 0 0 1 7 0"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="19.5" r="1.1" fill="currentColor" />
    </svg>
  );
}

function AlertIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
      <path
        d="M12 3.8 2.6 20h18.8L12 3.8Z"
        stroke="currentColor"
        strokeWidth="2.1"
        strokeLinejoin="round"
      />
      <path
        d="M12 10v4"
        stroke="currentColor"
        strokeWidth="2.1"
        strokeLinecap="round"
      />
      <circle cx="12" cy="17.2" r="1.05" fill="currentColor" />
    </svg>
  );
}

function ChevronIcon({ flipped }: { flipped: boolean }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      style={{ transform: flipped ? "rotate(180deg)" : undefined }}
    >
      <path
        d="m6 9 6 6 6-6"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
